import fetcher from 'api/fetcher';
import { AxiosError } from 'axios';
import { push } from 'connected-react-router';
import { getNullableNumberFromString } from 'utils/clickme';
import { MagritteNotificationName } from 'utils/constants/notification';

import auctionCreateCampaignButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_create_campaign_button_click';

import { translate } from 'hooks/translation/useTrls';

import {
    AuctionBuilderErrors,
    AuctionCampaign,
    AuctionCampaignStatus,
    auctionSelector,
    auctionSelectorNonNullable,
    CommonErrorCode,
    setErrors,
} from 'store/models/auctionData';
import {
    addMagritteCustomNotification,
    addMagritteNotification,
    MagritteNotificationType,
} from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

export const updateAuction = (
    campaignId: string,
    setLoading: (loading: boolean) => void,
    showBalanceReplenishmentModal: () => void,
    dashboardRoute: string
): AppThunkAction => {
    return (dispatch, getState) => {
        setLoading(true);

        const state = getState();
        const { value: bid } = auctionSelectorNonNullable.bid(state);
        const { value: budget } = auctionSelectorNonNullable.budget(state);
        const biddingStrategy = auctionSelectorNonNullable.biddingStrategy(state);
        const bidRating = auctionSelector.bidRating(state);
        const vacancyId = auctionSelector.vacancyId(state);
        const auctionCampaign = auctionSelector.auctionCampaign(state);
        const startAt = auctionSelector.startDate(state);
        const endAt = auctionSelector.endDate(state);
        const trls = state.translations.trls;

        const body = {
            vacancyId: getNullableNumberFromString(vacancyId),
            bid,
            budget,
            startAt,
            endAt,
            biddingStrategy,
        };

        const isActiveCampaign =
            auctionCampaign?.campaignStatus === AuctionCampaignStatus.Active ||
            auctionCampaign?.campaignStatus === AuctionCampaignStatus.ActiveWithDelayedStart;

        const request = isActiveCampaign
            ? () => fetcher.put<unknown, AuctionCampaign>(`/api/v1/auction/${campaignId}`, body)
            : () => fetcher.post<unknown, AuctionCampaign>(`/api/v1/auction/${campaignId}/restore`, body);

        return request()
            .then(() => {
                auctionCreateCampaignButtonClick({
                    vacancyId: String(vacancyId),
                    campaignId,
                    bidInCents: bid || 0,
                    budgetInCents: budget || 0,
                    maxBidAmongSimilarVacancies: bidRating?.maxBidAmongSimilarVacancies || 0,
                    startAt,
                    endAt,
                    audiencePerWeek: bidRating?.audience?.audiencePerWeek,
                    audienceCapacityPerWeek: bidRating?.audience?.audienceCapacityPerWeek,
                    biddingStrategy,
                });

                dispatch(push(dashboardRoute));
                dispatch(addMagritteNotification(MagritteNotificationName.AuctionBuilderSaveSuccess));
            })
            .catch((error: AxiosError<AuctionBuilderErrors>) => {
                setLoading(false);

                const response = error?.response;

                if (!response) {
                    return;
                }

                const errors = response.data;

                auctionCreateCampaignButtonClick({
                    vacancyId: String(vacancyId),
                    campaignId,
                    bidInCents: bid || 0,
                    budgetInCents: budget || 0,
                    errors: JSON.stringify(errors),
                    audiencePerWeek: bidRating?.audience?.audiencePerWeek,
                    audienceCapacityPerWeek: bidRating?.audience?.audienceCapacityPerWeek,
                    biddingStrategy,
                });

                if (response.status === 400) {
                    if (errors.message === CommonErrorCode.LowBalance) {
                        dispatch(
                            addMagritteCustomNotification({
                                type: MagritteNotificationType.Error,
                                text: translate(trls, 'AuctionBuilder.notification.error.lowBalanceError'),
                                actionLabel: translate(trls, 'AuctionBuilder.notification.error.topupBalance'),
                                onAction: showBalanceReplenishmentModal,
                            })
                        );
                    } else {
                        dispatch(addMagritteNotification(MagritteNotificationName.AuctionBuilderValidationError));
                    }
                    dispatch(setErrors(errors));
                } else {
                    dispatch(addMagritteNotification(MagritteNotificationName.AuctionBuilderSomethingWentWrongError));
                }
            });
    };
};
